import { FileDto, FolderDto, Role, RoleEnum, SpaceDto } from '@braoshzmvavz/picnube-common';
import { IconType } from "react-icons";
import { HiArrowsExpand, HiCalendar, HiClock, HiCloudUpload, HiDownload, HiFolderAdd, HiInformationCircle, HiPencil, HiPlay, HiShare, HiTrash, HiUsers, HiViewGrid } from "react-icons/hi";
import { MdSettingsRemote } from "react-icons/md";

export enum ActionId {
    SHARE,
    MANAGE_ACCESS,
    CREATE_FOLDER,
    UPDATE_FOLDER,
    DELETE_FOLDER,
    DOWNLOAD_FILE,
    MODIFY_CREATION_DATE,
    RENAME_FILE,
    UPLOAD_FILES,
    DELETE_FILE,
    SHARE_FILE,
    OPEN_FILE_DETAIL,
    VIEW_GALLERY,
    PLAY,
    REMOTE_CONTROL,
    MOVE_FOLDER,
    SET_DEFAULT_DATE,
}

export interface MenuSection {
    name: string;
    actions: MenuAction[];
}

export interface MenuContext {
    folder?: FolderDto;
    file?: FileDto;
    folderFiles?: FileDto[];
    space?: SpaceDto;
    demo: boolean;
}

export interface MenuAction {
    id?: ActionId;
    name: string;
    icon: IconType;
    description?: string;
    elementProps?: any;
    onClick?: () => void;
    showIf?: (ctx: MenuContext, email: string) => boolean;
    disabledIf?: (ctx: MenuContext, email: string) => boolean;
}

export const LOADER_MENU_CONFIG: MenuSection[] = [
    {
        name: "Actions",
        actions: [
            /*{
                name: "View in gallery",
                icon: HiViewGrid,
                id: ActionId.VIEW_GALLERY,
            },*/
            {
                name: "Upload files",
                icon: HiCloudUpload,
                id: ActionId.UPLOAD_FILES,
                disabledIf: (ctx: MenuContext) => {
                    if (ctx.demo) return true;
                    if (!ctx?.space) return false;
                    return ![RoleEnum.ADMIN, RoleEnum.OWNER, RoleEnum.WRITE].map(role => role.toString()).includes(ctx.space.role.toString());
                },
            },
            {
                name: "New folder",
                id: ActionId.CREATE_FOLDER,
                icon: HiFolderAdd,
                disabledIf: (ctx: MenuContext, email: string) => {
                    if (ctx.demo) return true;
                    if (!ctx?.space) return false;
                    return ![RoleEnum.ADMIN, RoleEnum.OWNER, RoleEnum.WRITE].map(role => role.toString()).includes(ctx.space.role.toString());
                },
            },
            {
                name: "Edit folder",
                id: ActionId.UPDATE_FOLDER,
                icon: HiPencil,
                showIf: (ctx: MenuContext) => {
                    return true;
                },
                disabledIf: (ctx: MenuContext) => {
                    if (ctx.demo) return true;
                    if (!ctx?.folder) return true;
                    if (!ctx?.space) return false;
                    return ![RoleEnum.ADMIN, RoleEnum.OWNER, RoleEnum.WRITE].map(role => role.toString()).includes(ctx.space.role.toString());
                },
            },
            {
                name: "Move folder",
                id: ActionId.MOVE_FOLDER,
                icon: HiArrowsExpand,
                showIf: (ctx: MenuContext) => {
                    return true;
                },
                disabledIf: (ctx: MenuContext, email: string) => {
                    if (ctx.demo) return true;
                    if (!ctx?.folder) return true;
                    if (!ctx?.space) return false;
                    return ![RoleEnum.ADMIN, RoleEnum.OWNER, RoleEnum.WRITE].map(role => role.toString()).includes(ctx.space.role.toString());
                },
            },
        ],
    },
    {
        name: "Slideshow options",
        actions: [
            {
                name: "Play",
                id: ActionId.PLAY,
                icon: HiPlay,
                showIf: (ctx: MenuContext) => !!ctx?.folder?.id,
                disabledIf: (ctx: MenuContext, email: string) => {
                    if (ctx.demo) return true;
                    if (ctx?.folderFiles?.length === 0) return true;
                    return false;
                },
            },
            {
                name: "Remote control",
                id: ActionId.REMOTE_CONTROL,
                icon: MdSettingsRemote,
                //description: "Use other device to control playback",
                showIf: (ctx: MenuContext) => !!ctx?.folder?.id,
                disabledIf: (ctx: MenuContext, email: string) => {
                    if (ctx.demo) return true;
                    if (ctx?.folderFiles?.length === 0) return true;
                    return false;
                },
            },
        ],
    },
    {
        name: "Share",
        actions: [
            {
                name: "Share",
                id: ActionId.SHARE,
                icon: HiShare,
                showIf: (ctx: MenuContext) => !!ctx?.folder?.id,
            },

            {
                // TODO: move to space
                name: "Manage permissions",
                id: ActionId.MANAGE_ACCESS,
                icon: HiUsers,
                showIf: (ctx: MenuContext) => !!ctx?.folder?.id,
                disabledIf: (ctx: MenuContext, email: string) => {
                    if (!ctx?.folder) return false;
                    if (!ctx?.space) return false;
                    return [RoleEnum.ADMIN, RoleEnum.OWNER, RoleEnum.WRITE].map(role => role.toString()).includes(ctx.space.role.toString());
                },
            },
        ],
    },
    {
        name: "Danger zone",
        actions: [
            {
                name: "Delete folder",
                id: ActionId.DELETE_FOLDER,
                elementProps: { color: "red" },
                icon: HiTrash,
                showIf: (ctx: MenuContext) => {
                    return true;
                },
                disabledIf: (ctx: MenuContext, email: string) => {
                    if (ctx.demo) return true;
                    if (!ctx?.space) return false;
                    return RoleEnum.OWNER.toString() !== ctx.space.role.toString();
                }
            },
        ],
    },
];

export const BOTTOM_DRAWER_QUICK_ACTIONS_CONFIG: MenuAction[] = [
    {
        name: "Open in gallery",
        id: ActionId.VIEW_GALLERY,
        icon: HiViewGrid,
    },
    {
        name: "Manage permissions",
        id: ActionId.MANAGE_ACCESS,
        icon: HiUsers,
        showIf: (ctx: any) => {
            if (!ctx?.folder) return false;
            return ctx?.folder?.accessLevel === 'write';
        },
    },
    {
        name: "Share",
        id: ActionId.SHARE,
        icon: HiShare,
    },
];

export const FILE_OPTIONS_MENU_CONFIG: MenuSection[] = [
    {
        name: "Actions",
        actions: [
            {
                name: "View details",
                id: ActionId.OPEN_FILE_DETAIL,
                icon: HiInformationCircle,
            },
            {
                name: "Share",
                id: ActionId.SHARE_FILE,
                icon: HiShare,
            },
            {
                name: "Download",
                id: ActionId.DOWNLOAD_FILE,
                icon: HiDownload,
            },
        ],
    },
    {
        name: "Slideshow options",
        actions: [
            {
                name: "Play from here",
                id: ActionId.PLAY,
                icon: HiPlay,
            },
        ]
    },
    {
        name: "Edit",
        actions: [
            {
                name: "Rename",
                id: ActionId.RENAME_FILE,
                icon: HiPencil,
                disabledIf: (ctx: any, email: string) => {
                    if (ctx.demo) return true;
                    return false;
                },
            },
            {
                name: "Modify creation date",
                id: ActionId.MODIFY_CREATION_DATE,
                icon: HiClock,
                disabledIf: (ctx: any, email: string) => {
                    if (ctx.demo) return true;
                    return false;
                },
            },
        ],
    },
    {
        name: "Danger zone",
        actions: [
            {
                name: "Delete file",
                id: ActionId.DELETE_FILE,
                elementProps: { color: "red" },
                icon: HiTrash,
                disabledIf: (ctx: any, email: string) => {
                    if (ctx.demo) return true;
                    return false;
                },
            },
        ],
    },
];
